import styled, { css } from 'styled-components'
import { Button as OGButton } from '@chegg-ui/button'
import { SecondaryButtonLink } from '@chegg-ui/button-link'
import { breakpoints, colors, spacing } from '@chegg-ui/theme'
import { Modal as OGModal } from '@chegg-ui/modal'
import { NEW_CITATION_LIST_MODAL_ID } from './constants'

const buttonOverflowFix = css`
  /**
   * [1]: counteract scale(1.05) applied on hover to prevent overflow issue
   */
  width: auto;
  max-width: calc(100% / 1.05); /* [1] */
`

/**
 * [1]: Values here are eyeballed and were tweaked px by px to get it positioned
 * [2]: 96px is natural width, scale it down
 * [3]: 42px is natural height, scale it down
 */

export const FreeCTA = styled(SecondaryButtonLink).attrs({
  variant: 'outlined',
})`
  ${buttonOverflowFix};
  margin: ${spacing.px16};
  border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};

  &&&:hover {
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    color: ${colors.white};
    box-shadow: 0 0 0 0.13rem ${({ theme }) => theme.Colors.forInterface.interfaceColor01}
      inset;
  }

  &&&:active {
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
    color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    box-shadow: 0 0 0 0.13rem ${({ theme }) => theme.Colors.forInterface.interfaceColor01}
      inset;
  }

  && {
    color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    box-shadow: 0 0 0 0.13rem ${({ theme }) => theme.Colors.forInterface.interfaceColor01}
      inset;
  }
`

const IN_BETWEEN_ELEMENTS_SELECTOR = '* + *'
const MODAL_DIALOG_SELECTOR = `& [id='${NEW_CITATION_LIST_MODAL_ID}']`
const MODAL_SPACING_SELECTOR = `${MODAL_DIALOG_SELECTOR} > *`
const MODAL_TITLE_SELECTOR = `& [id='${NEW_CITATION_LIST_MODAL_ID}-title']`
const MODAL_BODY_SELECTOR = `${MODAL_TITLE_SELECTOR} ~ :nth-child(2)`

export const HackyFooter = styled.div``

export const HackyModal = styled(OGModal)`
  /**
   * [1]: The default modal size between v2 and v3 widened a bit, it makes this modal look weird
   * [2]: At the smallest size, some of the top nav bar stuff can be on top of the modal, space it out a bit
   */
  ${MODAL_DIALOG_SELECTOR} {
    max-width: 550px; /* [1] */

    @media (max-width: 599px) {
      padding-top: ${spacing.px32}; /* [2] */
    }
  }

  /**
   * Establish a consistent vertical rhythm (vertical gaps between major items in UI) based on designs
   * [1]: prefer establishing vertical rhythm via group rules (the "> * + *" selector)
   * [2]: need to set margin on ModalContents and other children because of overflow issue and free tag on button
   */
  ${MODAL_TITLE_SELECTOR} {
    margin-bottom: 0; /* [1] */
    text-align: center;
  }

  ${MODAL_BODY_SELECTOR} > * /* [2] */ {
    margin-top: ${spacing.px32};
  }

  /**
   * Get the footer contents to be at the bottom and span to the dialog's boundaries
   * [1]: Allow the scrollable area to extend to the bounds of the dialog's container
   * [2]: Replace margin spacing with padding for contents to still appear well spaced
   * [3]: Allow the the footer contents to stretch to the parent's edges without being hidden
   * [4]: Smallest size causes buttons to wrap, prevent that
   */
  ${MODAL_SPACING_SELECTOR} {
    padding: ${spacing.px32} 0 0 0;
  }

  ${MODAL_BODY_SELECTOR} {
    padding-left: ${spacing.px32}; /* [2] */
    padding-right: ${spacing.px32}; /* [2] */
    padding-bottom: ${spacing.px32}; /* [2] */

    & ${HackyFooter} {
      margin-left: -${spacing.px32}; /* [3] */
      margin-right: -${spacing.px32}; /* [3] */
      margin-bottom: -${spacing.px32}; /* [3] */
    }

    @media only screen and (min-width: ${breakpoints.sm.min}) /* [4] */ {
      padding-left: ${spacing.px8};
      padding-right: ${spacing.px8};
    }
  }
`

export const ModalContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  & > ${IN_BETWEEN_ELEMENTS_SELECTOR} {
    margin-top: ${spacing.px16};
  }
`
export const ContinueCTA = styled(OGButton)`
  ${buttonOverflowFix};
  margin: ${spacing.px16};

  border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  color: ${colors.white};

  &&& {
    background: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
  }

  &&&:hover,
  &&&:focus {
    border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
    color: ${colors.white};
  }

  &&&:active {
    border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    color: ${colors.white};
  }
`

export const FreeText = styled.h2`
  margin-bottom: 0;
  text-align: center;
  display: block;
  margin: 0 0 ${spacing.px16} 0;
  color: ${colors.dark};
  font-family: 'Aspira Webfont', 'Helvetica', 'Arial', sans-serif;
  font-weight: bold;
  font-size: ${spacing.px32};
  line-height: 1.2;
`
export const InfoText = styled.span`
  text-align: center;
  color: ${colors.grey};
  padding: 0 ${spacing.px32};
`

export const CTAContainer = styled.div`
  @media (max-width: 450px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`
